import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import * as M from '../mock';
import { api } from '../lib/api';

const KEY = 'eh_site_config_v1';

const DEFAULTS = {
  site: { ...M.SITE, logoUrl: '/brand-logo.jpg', brandColor: '#00ff9d', adminPass: 'admin123' },
  notifications: { telegram: { enabled: false, bot_token: '', chat_id: '' } },
  analytics: { enabled: false, googleId: '' },
  feedProfile: { username: 'errorhacker', displayName: 'ERRORHACKER', bio: '// cyber-defense studio\n// education · tools · consultation\n// authorised audits only', website: 'https://errorhacker.site', followers: 128400, following: 42, verified: true, instagram_url: '', follow_label: 'FOLLOW', message_kind: 'telegram', message_url: '', message_label: 'MESSAGE' },
  nav: M.NAV,
  services: M.SERVICES,
  books: M.BOOKS,
  memberships: M.MEMBERSHIPS,
  blogs: M.BLOGS,
  tools: M.TOOLS,
  partners: M.PARTNERS,
  faqs: M.FAQS,
  testimonials: M.TESTIMONIALS,
  activity: M.ACTIVITY,
  stats: M.STATS,
  howSteps: [
    { n: '01', icon: 'MessageSquare', t: 'Book & Brief', d: 'Tell us the incident, the target platform, and timeline. Written scope confirmed within 24h.' },
    { n: '02', icon: 'Lock', t: 'Secure Session', d: 'Payment routed through licensed providers. Communication over encrypted channels. Nothing outside the agreed scope.' },
    { n: '03', icon: 'Cpu', t: 'Live Coaching', d: 'Video walk-through of the platform\'s official recovery flow, evidence gathering and account hardening — with you at the driver\'s seat.' },
    { n: '04', icon: 'BadgeCheck', t: 'After-Report', d: 'Written after-report with next steps and a follow-up check-in included. Money-back if we can\'t help in the first session.' },
  ],
  hero: {
    welcome: 'WELCOME TO',
    kicker: 'CYBER-DEFENSE STUDIO // SESSION SECURE_',
    typewriterLines: [
      'initializing defence session...',
      'AI security tools loaded.',
      'consultation desk online.',
      'education library ready.',
    ],
    primaryCta: 'EXPLORE FREE TOOLS',
    primaryCtaTo: '/tools',
    ghostCta: '> book_advisory',
    ghostCtaTo: '/advisory',
  },
  comparison: [
    { f: 'Weekly threat-intel briefings', r: true, o: true, e: true },
    { f: 'Community Telegram', r: true, o: true, e: true },
    { f: 'All AI security tools', r: false, o: true, e: true },
    { f: 'Full ebook library', r: false, o: true, e: true },
    { f: '24/7 priority support', r: false, o: true, e: true },
    { f: '1-on-1 mentorship', r: false, o: false, e: true },
    { f: 'Custom lab environment', r: false, o: false, e: true },
    { f: 'Private CTF track', r: false, o: false, e: true },
    { f: 'Lifetime updates', r: false, o: false, e: true },
  ],
};

const SiteConfigCtx = createContext(null);

const mergeWithDefaults = (data) => ({
  ...DEFAULTS,
  ...data,
  site: { ...DEFAULTS.site, ...(data?.site || {}) },
  hero: { ...DEFAULTS.hero, ...(data?.hero || {}) },
  feedProfile: { ...DEFAULTS.feedProfile, ...(data?.feedProfile || {}) },
  analytics: { ...DEFAULTS.analytics, ...(data?.analytics || {}) },
  notifications: {
    ...DEFAULTS.notifications,
    ...(data?.notifications || {}),
    telegram: { ...DEFAULTS.notifications.telegram, ...((data?.notifications || {}).telegram || {}) },
  },
});

export const SiteConfigProvider = ({ children }) => {
  // Try to seed from localStorage cache for instant render
  const [config, setConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return mergeWithDefaults(JSON.parse(raw));
    } catch (_) {}
    return DEFAULTS;
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch from API on mount (overwrites cache)
  useEffect(() => {
    let active = true;
    api.getConfig()
      .then(remote => {
        if (!active) return;
        const merged = mergeWithDefaults(remote);
        setConfig(merged);
        try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch (_) {}
      })
      .catch(err => console.warn('config fetch failed, using local cache', err))
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  // Persist + apply brand color
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(config)); } catch (_) {}
    const c = config.site?.brandColor;
    if (c) document.documentElement.style.setProperty('--eh-green', c);
  }, [config]);

  // Push to backend (admin only)
  const persistRemote = useCallback(async (next) => {
    setSaving(true);
    try { await api.putConfig(next); } catch (e) { console.warn('remote save failed', e?.message); }
    finally { setSaving(false); }
  }, []);

  const api2 = useMemo(() => ({
    config,
    loaded,
    saving,
    setConfig: (val) => {
      const next = typeof val === 'function' ? val(config) : val;
      setConfig(next);
      // Only push to backend if admin is logged in
      if (localStorage.getItem('eh_admin_token')) persistRemote(next);
    },
    update: (path, value) => {
      setConfig(prev => {
        const next = { ...prev };
        const keys = path.split('.');
        let cur = next;
        for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = { ...cur[keys[i]] }; cur = cur[keys[i]]; }
        cur[keys[keys.length - 1]] = value;
        if (localStorage.getItem('eh_admin_token')) persistRemote(next);
        return next;
      });
    },
    setList: (listKey, items) => {
      setConfig(prev => {
        const next = { ...prev, [listKey]: items };
        if (localStorage.getItem('eh_admin_token')) persistRemote(next);
        return next;
      });
    },
    reset: async () => {
      localStorage.removeItem(KEY);
      setConfig(DEFAULTS);
      if (localStorage.getItem('eh_admin_token')) await persistRemote(DEFAULTS);
    },
    refetch: async () => {
      try {
        const remote = await api.getConfig();
        const merged = mergeWithDefaults(remote);
        setConfig(merged);
      } catch (_) {}
    },
  }), [config, loaded, saving, persistRemote]);

  return <SiteConfigCtx.Provider value={api2}>{children}</SiteConfigCtx.Provider>;
};

export const useSiteConfig = () => {
  const ctx = useContext(SiteConfigCtx);
  if (!ctx) throw new Error('useSiteConfig must be inside SiteConfigProvider');
  return ctx;
};

export { DEFAULTS };
